import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, error: authError } = useAuth();
  const [formData, setFormData] = useState({
    login: '',
    password: '',
    location: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isLocating, setIsLocating] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/client/');
    }
  }, [isAuthenticated, navigate]);

  // Display any authentication errors
  useEffect(() => {
    if (authError) {
      setFeedback({ type: 'error', message: authError });
    }
  }, [authError]);

  useEffect(() => {
    // Try to get user's geolocation when component mounts
    getGeolocation();
  }, []);

  const getGeolocation = () => {
    if (navigator.geolocation) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        // Success callback
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData(prev => ({
            ...prev,
            location: `${latitude},${longitude}`
          }));
          setIsLocating(false);
        },
        // Error callback
        (error) => {
          console.log('Geolocation error:', error.message);
          setIsLocating(false);
        },
        // Options
        { 
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      console.log('Geolocation is not supported by this browser');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear feedback when user starts typing again
    if (feedback.message) {
      setFeedback({ type: '', message: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.login.trim()) {
      newErrors.login = 'Обязательное поле';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Обязательное поле';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });
    
    try {
      // Use the login function from AuthContext
      const result = await login(formData);
      
      if (!result.success) {
        throw new Error(result.error || 'Ошибка при входе');
      }
      
      // Success
      setFeedback({
        type: 'success',
        message: 'Вход выполнен успешно'
      });
      
      // Redirect to client page will be handled by the useEffect based on isAuthenticated
      
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Произошла ошибка при входе'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-center my-5 py-5">
        <div className="w-full md:w-2/3 lg:w-1/2 bg-gray-200 py-5 px-5 rounded-lg pb-10">
          {feedback.message && (
            <div className={`mb-4 p-3 rounded ${feedback.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
              {feedback.message}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="w-full">
            <div className="flex flex-col items-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Вход</h2>
              
              <input 
                type="hidden" 
                name="location" 
                value={formData.location} 
              />
              
              {isLocating && (
                <div className="w-full px-0 lg:px-3 mb-2 text-sm text-gray-600">
                  Определение местоположения...
                </div>
              )}
              
              <div className="w-full px-0 lg:px-3 mb-4">
                <label htmlFor="form-login" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans'] ">
                  Логин / Эл. почта
                </label>
                <input
                  type="text"
                  id="form-login"
                  name="login"
                  value={formData.login}
                  onChange={handleChange}
                  placeholder="Логин"
                  className={`w-full p-2 border rounded my-3 ${errors.login ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
                {errors.login && (
                  <div className="text-red-500 text-sm mt-1">{errors.login}</div>
                )}
              </div>
              
              <div className="w-full px-0 lg:px-3 mb-4">
                <label htmlFor="form-pass" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans'] ">
                  Пароль
                </label>
                <input
                  type="password"
                  id="form-pass"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Пароль"
                  className={`w-full p-2 border rounded my-3 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
                {errors.password && (
                  <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                )}
              </div>
              
              <div className="w-full flex flex-col md:flex-row md:justify-between px-0 lg:px-3  mb-4">
                <div className="mb-2 md:mb-0">
                  <Link to="/registration/" className="text-blue-600 hover:underline">
                    Регистрация
                  </Link>
                </div>
                <div>
                  <Link to="/forgot_password/" className="text-blue-600 hover:underline">
                    Забыли пароль?
                  </Link>
                </div>
              </div>
              
              <div className="w-full px-0 lg:px-3">
                <button
                  type="submit"
                  disabled={isSubmitting || isLocating}
                  className={`w-full py-2 px-4 bg-[#c0834d] text-white rounded hover:bg-[#a87442] transition-colors ${(isSubmitting || isLocating) ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Вход...' : 'Войти'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;